import * as React from 'react';
import { useEffect, useMemo } from 'react';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
	ResultsLegFragment,
	AviaSearchParameters,
	FlightClass,
	InfoMessagePositionsEnum,
	AviaPassengerFareFragment
} from '@websky/graphql';
import { OnFareSelect } from '../../Results/types';
import { SelectedFare } from '../../DesktopFlight/types';
import { useTheme } from '../../theme';
import { useDesktopFlightWithPopup } from '../../DesktopFlight/components/DesktopFlightPopup/hooks';
import Segment from './Segment/Segment';
import SegmentDelimiterItem from './SegmentDelimiterItem/SegmentDelimiterItem';
import FlightPrice from './FlightPrice/FlightPrice';
import DesktopFlightModalContent from '../../DesktopFlight/components/DesktopFlightModalContent/DesktopFlightModalContent';
import DesktopFlightModalContentSelected from '../../DesktopFlight/components/DesktopFlightModalContentSelected/DesktopFlightModalContentSelected';
import Dropdown from '../../Dropdown';
import { useRenderers } from '../../renderProps';
import InfoMessages from '../../InfoMessages';
import { DesktopFlightFullscreen } from '../../DesktopFlight';
import { CdnIcon } from '@shared/ui';
import { CheckinCard } from '@shared/CheckinCard';
import { flightCardDataAttributes } from '@shared/lib/flight';

export interface Props {
	legId?: number;
	flightIndex?: number;
	flight: ResultsLegFragment;
	flightIcon?: React.ReactNode;
	selectedFares: SelectedFare[];
	passengerFares?: AviaPassengerFareFragment[];
	preselectedFlightClass?: FlightClass;
	isRecommended?: boolean;
	singleFlight?: boolean;
	onSelect?: OnFareSelect;
	orderId?: string;
	isSelected?: boolean;
	canceled?: boolean;
	hidePrice?: boolean;
	readonly?: boolean;
	searchParameters?: AviaSearchParameters;
	hideOnlyPrice?: boolean;
	onClear?: () => void;
	onSubsidySuggestModalOpen?: (next: () => void) => void;
	showSeatsLeftForAllFares?: boolean;
	hideConditionsButton?: boolean;
	isReturnFlight?: boolean;
	isSelectorInModal?: boolean;
	isFareRulesDisabled?: boolean;
	isOrderLoading?: boolean;
	isFaresMilesMustBeRequested?: boolean;
	onSetViewFlight?: React.Dispatch<ResultsLegFragment>;
}

const DesktopFlightRoute: React.FC<Props> = props => {
	const { t } = useTranslation('DesktopFlightInfo');

	const {
		legId,
		flightIndex,
		flight,
		flightIcon,
		isRecommended,
		isSelected,
		isSelectorInModal = true,
		singleFlight,
		selectedFares,
		passengerFares,
		canceled,
		searchParameters,
		onClear,
		hideOnlyPrice,
		isFareRulesDisabled,
		isOrderLoading,
		isFaresMilesMustBeRequested,
		onSubsidySuggestModalOpen,
		onSetViewFlight
	} = props;

	const { DesktopFlightRouteStyles: theme } = useTheme('DesktopFlightRoute');
	const { DesktopFlightRouteModal: DesktopFlightRouteModalOverride = DesktopFlightModalContent } = useRenderers();

	const {
		openModal,
		isModalOpen,
		subStatuses,
		flightSegments,
		lastSelectedFare: lastSelectedFareId,
		closeModal,
		businessFareGroups,
		economyFareGroups,
		milesFareGroups,
		getOnFareSelect,
		upgradeOptions,
		isComparisonOpen,
		setIsComparisonOpen
	} = useDesktopFlightWithPopup(props, !isSelectorInModal && singleFlight && !onSubsidySuggestModalOpen);

	const FlightContainer = React.useCallback(
		(children: React.ReactNode) => {
			return isSelectorInModal || props.isSelected ? (
				<>{children}</>
			) : (
				<Dropdown
					isOpen
					onClose={
						(!singleFlight && !isComparisonOpen) || !!onSubsidySuggestModalOpen ? closeModal : () => {}
					}
					outsideClickIgnoreClass={theme.fares.split(' ')[0]}
				>
					{children}
				</Dropdown>
			);
		},
		[props.isSelected]
	);

	const lastSegment = flightSegments[flightSegments.length - 1]?.segment;

	const isReadOnly = useMemo(() => {
		const flightInProcess = subStatuses.some(status => status === 'AwaitingExchange' || status === 'Upgrade');

		return props.readonly || flightInProcess;
	}, [props.readonly, subStatuses]);

	const lastSelectedFare = useMemo(() => {
		return props.flight.pricesForFareGroups.find(
			fareGroup => fareGroup.fareFamily && fareGroup.fareFamily.id === lastSelectedFareId
		)?.fareFamily;
	}, [props.flight.pricesForFareGroups, lastSelectedFareId]);

	const flightId = flight.pricesForFareGroups.find(price => price?.fareFamily?.id === lastSelectedFare?.id)
		?.prices?.[0].flight.id;

	const faresContent = isSelected ? (
		<DesktopFlightModalContentSelected
			legId={legId}
			flightId={flightId}
			lastSelectedFare={lastSelectedFare}
			passengerFares={passengerFares}
			flight={flight}
			orderId={props.orderId}
			onClose={closeModal}
			hideConditionsButton={props.hideConditionsButton}
			isReturnFlight={props.isReturnFlight}
			isSelectorInModal={isSelectorInModal}
			hidePrice={props.hidePrice}
			isFareRulesDisabled={isFareRulesDisabled}
			isFaresMilesMustBeRequested={isFaresMilesMustBeRequested}
		/>
	) : (
		<DesktopFlightRouteModalOverride
			legId={legId}
			lastSelectedFare={lastSelectedFare}
			selectedFares={selectedFares}
			flight={flight}
			flightId={flightId}
			orderId={props.orderId}
			economyFares={economyFareGroups}
			businessFares={businessFareGroups}
			milesFares={milesFareGroups}
			getOnFareSelect={getOnFareSelect}
			upgradeOptions={upgradeOptions}
			hidePrice={props.hidePrice}
			searchParameters={searchParameters}
			onClose={closeModal}
			preselectedFlightClass={props.preselectedFlightClass}
			isReturnFlight={props.isReturnFlight}
			isSelectorInModal={isSelectorInModal}
			isFaresMilesMustBeRequested={isFaresMilesMustBeRequested}
			setIsComparisonOpen={setIsComparisonOpen}
		/>
	);

	const onToggle = () => {
		if (!isReadOnly && !canceled) {
			if (onSubsidySuggestModalOpen) {
				onSubsidySuggestModalOpen(openModal);
			} else if (isModalOpen) {
				closeModal();
			} else {
				openModal();
			}
		}
	};

	useEffect(() => {
		onSetViewFlight?.(isModalOpen ? flight : null);
	}, [isModalOpen]);

	/**
	 * WEBSKY-6907: 'flightSearch - отображаем аналогично flight`,
	 * но только на страницах результатов поиска и выбранных рейсах
	 * не отображаем на странице управления заказом
	 */
	const infoMessagesPositions =
		window !== undefined && window.location.href.includes('/results')
			? [InfoMessagePositionsEnum.flightSearch, InfoMessagePositionsEnum.flight]
			: [InfoMessagePositionsEnum.flight];

	return (
		<>
			{FlightContainer(
				<CheckinCard
					className={cn(theme.container, theme.flight, {
						[theme.flight_readonly]: isReadOnly,
						[theme.flight_canceled]: canceled,
						[theme.flight_opened]: isModalOpen,
						[theme.flight_dropdown]: !isSelectorInModal,
						[theme.selected]: props.isSelected
					})}
					{...flightCardDataAttributes({
						testId: 'flight',
						segments: flight.segments,
						flightIndex,
						selected: props.isSelected,
						recommended: props.isRecommended,
						opened: isModalOpen
					})}
				>
					<div className={theme.flightContent} onClick={onToggle}>
						{flightIcon}
						<div className={cn(theme.segments, { [theme.segments_withoutPrices]: props.hidePrice })}>
							<InfoMessages
								position={infoMessagesPositions}
								className={theme.infoMessage}
								itemHtmlClassName={theme.infoMessage__htmlMessage}
								messages={flight.segments[0].segment.messages}
							/>

							{flightSegments.map((segment, key) => {
								const isLast = key === flightSegments.length - 1;
								const multipleSegments = flightSegments.length > 1;

								return (
									<SegmentDelimiterItem
										key={segment.segment.id}
										segment={segment}
										isLast={isLast}
										multipleSegments={multipleSegments}
										flight={flight}
									>
										<Segment
											segment={segment}
											first={key === 0}
											last={isLast}
											stops={segment.segment?.stops || []}
											singleFlight={singleFlight}
											nextDay={
												key >= 1 &&
												flightSegments[key - 1]?.segment.departure.date !==
													segment.segment.arrival.date
											}
											multipleSegments={multipleSegments}
											isReturnFlight={props.isReturnFlight}
										/>
									</SegmentDelimiterItem>
								);
							})}

							{(flightSegments.length > 1 || lastSegment?.stops?.length > 0) && false && (
								<div className={theme.duration} />
							)}
						</div>

						{!props.hidePrice && !subStatuses.length && (
							<>
								<FlightPrice
									best={isRecommended}
									singleFlight={singleFlight}
									flight={flight}
									selectedFares={selectedFares}
									passengerFares={passengerFares}
									isSelected={isSelected}
									canceled={props.canceled}
									onClear={onClear}
									hideOnlyPrice={hideOnlyPrice}
									isOrderLoading={isOrderLoading}
									preselectedFlightClass={props.preselectedFlightClass}
								/>

								{!isSelectorInModal && (
									<div className={theme.dropdown__icon}>
										<span className={cn(theme.arrow, { [theme.arrow_opened]: isModalOpen })}>
											<CdnIcon icon="Arrow/Chevron_right" />
										</span>
									</div>
								)}
							</>
						)}

						{!!subStatuses.length && (
							<div className={theme.subStatus}>
								<span>{t(`SubStatus_${subStatuses[0]}`)}</span>
							</div>
						)}
					</div>

					{!isSelectorInModal && (
						<Collapse className={theme.fares} in={isModalOpen} mountOnEnter>
							{faresContent}
						</Collapse>
					)}
				</CheckinCard>
			)}

			{isSelectorInModal && (
				<DesktopFlightFullscreen isOpen={isModalOpen} onClose={closeModal} isSelected={isSelected}>
					{faresContent}
				</DesktopFlightFullscreen>
			)}
		</>
	);
};

export default DesktopFlightRoute;
